/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Flex, Text, TextField } from '@servicetitan/anvil2';
import { DuctulatorRequestDto } from '../../models/calculator.dto';
import { FormState } from 'formstate';
import { InputFieldState } from '@servicetitan/form-state';
import { useDependencies } from '@servicetitan/react-ioc';
import { CalculatorStore } from '../../stores/calculator.store';
import { observer } from 'mobx-react';

export const CalculatorDuctSizingForm: FC<{
    className?: string;
    onSubmit?: (params: DuctulatorRequestDto) => void;
}> = observer(({ className }) => {
    const [store] = useDependencies<[CalculatorStore]>(CalculatorStore);
    const [isLoading, setIsLoading] = useState(false);

    const formState = useMemo(() => {
        return new FormState({
            airflow_rate: new InputFieldState<string>('').validators(value => !value && 'Required'),
            maximum_air_velocity: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
            maximal_static_loss: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        const validateResult = await formState.validate();
        if (validateResult.hasError) {
            return;
        }
        const params: DuctulatorRequestDto = {
            tool_name: 'ductulator',
            tool_input: {
                airflow_rate: Number(formState.$.airflow_rate.$),
                maximum_air_velocity: Number(formState.$.maximum_air_velocity.$),
                maximal_static_loss: Number(formState.$.maximal_static_loss.$),
            },
        };
        setIsLoading(true);
        await store.submitCalculation(params);
        setIsLoading(false);
    }, [formState, store]);

    return (
        <Flex direction="column" gap="4" className={classNames('w-100-i', className)}>
            <Text size="small" subdued>
                Please provide the following info:
            </Text>
            <TextField
                required
                error={formState.$.airflow_rate.error}
                label="Required Airflow (CFM)"
                type="number"
                value={formState.$.airflow_rate.value}
                onChange={e => formState.$.airflow_rate.onChange(e.target.value)}
            />
            <TextField
                required
                error={formState.$.maximum_air_velocity.error}
                label="Maximum Allowable Velocity (FPM)"
                type="number"
                value={formState.$.maximum_air_velocity.value}
                onChange={e => formState.$.maximum_air_velocity.onChange(e.target.value)}
            />
            <TextField
                required
                error={formState.$.maximal_static_loss.error}
                label="Maximum Static Pressure Loss"
                type="number"
                value={formState.$.maximal_static_loss.value}
                onChange={e => formState.$.maximal_static_loss.onChange(e.target.value)}
            />
            <Button
                loading={isLoading}
                alignSelf="self-end"
                appearance="primary"
                size="small"
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Flex>
    );
});
